import React, { useCallback, useState } from 'react';
import css from './FilterSidebar.module.css';
import {
  BrandOfferingFilterCategories,
  FieldConfigKey,
  filterGroupSidebarSectionLabels,
  PARAM_KEYS,
  UserType,
} from '../../../util/enums';
import { FormattedMessage } from 'react-intl';
import DivisionFilterSection from './DivisionFilterSection';
import { createFilterOptions, getSelectedValues } from '../../../util/genericHelpers';
import ConferenceFilterSection from './ConferenceFilterSection';
import GroupTypeFilterOption from './GroupTypeFilterOption';
import { jobCategories } from '../../../config/configSearch';
import { createResourceLocatorString } from '../../../util/routes';
import StateFilterSection from './StateFilterSection';
import SearchBar from '../SearchBar';
import { OPPORTUNITIES, SCHOOL } from '../../../util/constants';

const GroupFilterSidebar = ({
  resetAllFilters,
  handleCheckboxChange,
  handleClearFilter,
  updateURLParams,
  history,
  routeConfiguration,
  config,
  handleRadioChange,
  location,
  intl,
  onHandleSearchChange,
  associatedListingCategory,
  activeCategory,
}) => {
  // Track which main sections are active
  const [activeSections, setActiveSections] = useState({
    industry: false,
    location: false,
    brandPremier: false,
    opportunities: false,
    groupPlus: false,
    groupStandard: false,
  });

  // Toggle main sections
  const toggleSection = useCallback(
    key => {
      setActiveSections(prev => {
        const nextActive = !prev[key];
        const updatedSections = { ...prev, [key]: nextActive };

        // Update URL for subscription types
        if (['groupPlus', 'groupStandard'].includes(key)) {
          const currentSubscriptions = Object.keys(updatedSections)
            .filter(k => ['groupPlus', 'groupStandard'].includes(k) && updatedSections[k])
            .map(k => filterGroupSidebarSectionLabels[k]);
          if (currentSubscriptions.length > 0) {
            updateURLParams(PARAM_KEYS.subscriptionType, currentSubscriptions);
          } else {
            handleClearFilter(PARAM_KEYS.subscriptionType);
          }
        }
        if (key === OPPORTUNITIES) {
          history.push(
            createResourceLocatorString(
              'BrandOfferingPage',
              routeConfiguration,
              {},
              {
                pub_listingCategory: SCHOOL,
                jobCategoryFilter: BrandOfferingFilterCategories.CAMPAIGNS,
              }
            )
          );
        }
        return updatedSections;
      });
    },
    [handleClearFilter, updateURLParams]
  );

  const selectedState = getSelectedValues(PARAM_KEYS.state, location);
  const selectedDivision = getSelectedValues(PARAM_KEYS.division, location);
  const selectedConference = getSelectedValues(PARAM_KEYS.conference, location);
  const selectedGroupType = getSelectedValues(PARAM_KEYS.groupType, location);
  const userFields = config.user.userFields || [];
  const groupTypeFieldConfig = userFields.find(field => field.key === FieldConfigKey.USER_TYPE_SCT);
  const groupTypeFilterOptions = createFilterOptions(groupTypeFieldConfig.enumOptions) || [];

  // When clicking a job category, update the URL parameter "pub_listingCategory"
  const handleJobCategoryClick = jobCategoryKey => {
    history.push(
      createResourceLocatorString(
        'BrandOfferingPage',
        routeConfiguration,
        {},
        { pub_listingCategory: 'school', jobCategoryFilter: jobCategoryKey }
      )
    );
  };
  const resetAll = () => {
    resetAllFilters();
    setActiveSections({});
  };
  return (
    <div className={css.filterSidebar}>
      {/* <div className={css.filterHeader}>
        <h2 className={css.title}>
          <FormattedMessage id="FilterCategories.filters" />
        </h2>
        <button className={css.resetAllButton} onClick={resetAll}>
          <FormattedMessage id="FilterCategories.resetAll" />
        </button>
      </div> */}

      {/* Search Bar */}
      <SearchBar
        onSearch={onHandleSearchChange}
        placeholder={intl.formatMessage({ id: 'FilterCategories.GroupFilterSidebar.search' })}
        activeCategory={activeCategory}
        hideCategories
      />

      {/* Pill Container (main sections) */}
      <div className={css.pillContainer}>
        {Object.keys(filterGroupSidebarSectionLabels).map(key => {
          // if (key === OPPORTUNITIES && associatedListingCategory !== SCHOOL) {
          //   return null;
          // }
          return (
            <button
              key={key}
              className={`${css.pill} ${activeSections[key] ? css.activePill : ''}`}
              onClick={() => toggleSection(key)}
            >
              {filterGroupSidebarSectionLabels[key]}
            </button>
          );
        })}
      </div>
      {/* Location Section */}
      {activeSections.location && (
        <div className={css.subSection}>
          <StateFilterSection
            handleCheckboxChange={handleCheckboxChange}
            handleClearFilter={handleClearFilter}
            selectedValues={selectedState}
            paramKey={PARAM_KEYS.state}
          />
        </div>
      )}

      {/* Division Section */}
      {activeSections.division && (
        <DivisionFilterSection
          updateURLParams={updateURLParams}
          handleClearFilter={handleClearFilter}
          selectedValues={selectedDivision}
          paramKey={PARAM_KEYS.division}
          handleCheckboxChange={handleCheckboxChange}
        />
      )}
      {/* Conference Section */}
      {activeSections.conference && (
        <ConferenceFilterSection
          selectedValues={selectedConference}
          paramKey={PARAM_KEYS.conference}
          handleClearFilter={handleClearFilter}
          handleCheckboxChange={handleCheckboxChange}
          history={history}
          routeConfiguration={routeConfiguration}
          listingCategory={UserType.SCHOOL}
        />
      )}
      {/* Group Type Section */}
      {activeSections.groupType && (
        <GroupTypeFilterOption
          groupTypeFilterOptions={groupTypeFilterOptions}
          handleClearFilter={handleClearFilter}
          paramKey={PARAM_KEYS.groupType}
          handleRadioChange={handleRadioChange}
          selectedValue={selectedGroupType}
        />
      )}

      {/* Jobs Section */}
      {activeSections.opportunities && (
        <div className={css.jobsSection}>
          <h3 className={css.jobsTitle}>
            <FormattedMessage id="FilterCategories.jobs" />
          </h3>
          <div className={css.jobCategoriesContainer}>
            {jobCategories.map(job => (
              <button
                key={job.key}
                className={css.jobCategoryButton}
                onClick={() => handleJobCategoryClick(job.key)}
              >
                {job.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default GroupFilterSidebar;
